'use strict';

/**
 * FrameworkClient -- the adoption seam onto the Fable RestClient + pict.EntityProvider.
 *
 * Plansheet historically hand-rolled `fetch()` in every provider, bypassing the
 * framework's networking layer entirely. That is why an expired session was
 * invisible: a raw fetch 401 just rejected somewhere a click handler swallowed.
 *
 * Routing providers through here funnels every data path onto the ONE RestClient
 * instance that carries the authenticationRecovery hook (pict.EntityProvider's
 * restClient, armed by Pict-Application-Plansheet._enableSessionRecovery). So a
 * 401 on any of these calls transparently drives the in-place re-auth modal and
 * replays the original request -- reads and writes alike -- and CRUD additionally
 * rides EntityProvider's cachetrax record cache.
 *
 * These are thin, promise-returning wrappers so migrated providers keep the exact
 * method signatures their views already call.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

// The shared RestClient (same instance the recovery hook is wired onto).
function _restClient(pPict)
{
	return (pPict && pPict.EntityProvider) ? pPict.EntityProvider.restClient : null;
}

function _entityProvider(pPict)
{
	return (pPict && pPict.EntityProvider) ? pPict.EntityProvider : null;
}

// Build an Error that carries the HTTP status and the server's message, matching
// the shape the old `throw new Error('HTTP ' + status)` produced so view-side
// catch/_toast handling is unchanged.
function _httpError(pStatus, pBody)
{
	let tmpMessage = 'HTTP ' + pStatus;
	if (pBody && typeof pBody === 'object' && pBody.Error) { tmpMessage = pBody.Error; }
	let tmpError = new Error(tmpMessage);
	tmpError.StatusCode = pStatus;
	return tmpError;
}

// EntityProvider's CRUD callbacks hand back the parsed body but not the HTTP
// response, so a non-2xx (e.g. a 401 whose recovery was declined) arrives as a
// meadow error envelope { Error, StatusCode } with a null error. Detect that
// shape and turn it into a rejection so views' catch/_toast paths still fire.
function _entityErrorFromBody(pBody)
{
	if (pBody && typeof pBody === 'object' && typeof pBody.StatusCode === 'number' && pBody.StatusCode >= 400)
	{
		return _httpError(pBody.StatusCode, pBody);
	}
	return null;
}

// Run a RestClient JSON verb as a promise. Any completed 401 has already been
// through the recovery hook by the time this callback fires (recovered -> the
// body here is the replayed 200; declined -> we surface the 401 as a rejection).
function _verb(pPict, pVerb, pURL, pBody)
{
	return new Promise((fResolve, fReject) =>
	{
		let tmpRestClient = _restClient(pPict);
		if (!tmpRestClient) { return fReject(new Error('Framework RestClient is not available.')); }
		let tmpOptions = { url: pURL };
		if (pBody !== undefined) { tmpOptions.body = pBody; }
		tmpRestClient[pVerb](tmpOptions, (pError, pResponse, pResultBody) =>
		{
			if (pError) { return fReject(pError); }
			let tmpStatus = pResponse ? pResponse.statusCode : 0;
			if (tmpStatus >= 400) { return fReject(_httpError(tmpStatus, pResultBody)); }
			return fResolve(pResultBody);
		});
	});
}

// ---- Custom endpoint verbs (any /1.0/ route) -----------------------------
// Pass fully-qualified paths, e.g. '/1.0/Features/0/500'. Every one is
// recovery-covered because it rides the shared RestClient.
function getJSON(pPict, pURL) { return _verb(pPict, 'getJSON', pURL); }
function postJSON(pPict, pURL, pBody) { return _verb(pPict, 'postJSON', pURL, pBody || {}); }
function putJSON(pPict, pURL, pBody) { return _verb(pPict, 'putJSON', pURL, pBody || {}); }
function delJSON(pPict, pURL) { return _verb(pPict, 'delJSON', pURL); }

// ---- Non-JSON reads (recovery-covered) -----------------------------------
// Plain-text GET (e.g. a rendered markdown/help doc). Rejects on >= 400 so
// callers can .catch(() => '') to preserve an empty-on-error contract.
function getText(pPict, pURL)
{
	return new Promise((fResolve, fReject) =>
	{
		let tmpRestClient = _restClient(pPict);
		if (!tmpRestClient) { return fReject(new Error('Framework RestClient is not available.')); }
		tmpRestClient.getRawText({ url: pURL }, (pError, pResponse, pBody) =>
		{
			if (pError) { return fReject(pError); }
			let tmpStatus = pResponse ? pResponse.statusCode : 0;
			if (tmpStatus >= 400) { return fReject(_httpError(tmpStatus, null)); }
			return fResolve(pBody == null ? '' : String(pBody));
		});
	});
}

// Binary GET returning an ArrayBuffer (e.g. a session zip for the parser). The
// RestClient hands back a Buffer; we slice out its backing ArrayBuffer so the
// browser-side consumers (parseZip, etc.) get exactly what fetch().arrayBuffer()
// would have given them.
function getBinary(pPict, pURL)
{
	return new Promise((fResolve, fReject) =>
	{
		let tmpRestClient = _restClient(pPict);
		if (!tmpRestClient) { return fReject(new Error('Framework RestClient is not available.')); }
		tmpRestClient.executeChunkedRequestBinary({ url: pURL, method: 'GET' }, (pError, pResponse, pBuffer) =>
		{
			if (pError) { return fReject(pError); }
			let tmpStatus = pResponse ? pResponse.statusCode : 0;
			if (tmpStatus >= 400) { return fReject(_httpError(tmpStatus, null)); }
			if (!pBuffer) { return fResolve(new ArrayBuffer(0)); }
			// Buffer -> ArrayBuffer (respecting byteOffset/length of the view).
			let tmpArrayBuffer = pBuffer.buffer.slice(pBuffer.byteOffset, pBuffer.byteOffset + pBuffer.byteLength);
			return fResolve(tmpArrayBuffer);
		});
	});
}

// ---- Binary upload (recovery-covered) ------------------------------------
// Wraps the RestClient's binary path (Blob/File/Buffer/ArrayBuffer bodies).
// The server returns a JSON status envelope, which we parse. A 401 here rides
// the same recovery + replay seam as the JSON verbs.
function uploadBinary(pPict, pURL, pBody, pMimeType, pMethod)
{
	return new Promise((fResolve, fReject) =>
	{
		let tmpRestClient = _restClient(pPict);
		if (!tmpRestClient) { return fReject(new Error('Framework RestClient is not available.')); }
		let tmpOptions =
		{
			url: pURL,
			method: pMethod || 'POST',
			body: pBody,
			headers: { 'Content-Type': pMimeType || 'application/octet-stream' }
		};
		tmpRestClient.executeBinaryUpload(tmpOptions, (pError, pResponse, pResultBody) =>
		{
			if (pError) { return fReject(pError); }
			let tmpStatus = pResponse ? pResponse.statusCode : 0;
			let tmpParsed = null;
			try { tmpParsed = pResultBody ? JSON.parse(pResultBody) : null; }
			catch (pParseError) { tmpParsed = pResultBody; }
			if (tmpStatus >= 400) { return fReject(_httpError(tmpStatus, tmpParsed)); }
			return fResolve(tmpParsed);
		});
	});
}

// ---- CRUD via EntityProvider (cachetrax-cached reads, invalidating writes) -
function getEntity(pPict, pEntityType, pIDRecord)
{
	return new Promise((fResolve, fReject) =>
	{
		let tmpEntityProvider = _entityProvider(pPict);
		if (!tmpEntityProvider) { return fReject(new Error('Framework EntityProvider is not available.')); }
		tmpEntityProvider.getEntity(pEntityType, pIDRecord, (pError, pRecord) =>
		{
			if (pError) { return fReject(pError); }
			let tmpBodyError = _entityErrorFromBody(pRecord);
			if (tmpBodyError) { return fReject(tmpBodyError); }
			return fResolve(pRecord);
		});
	});
}

function createEntity(pPict, pEntityType, pRecord)
{
	return new Promise((fResolve, fReject) =>
	{
		let tmpEntityProvider = _entityProvider(pPict);
		if (!tmpEntityProvider) { return fReject(new Error('Framework EntityProvider is not available.')); }
		tmpEntityProvider.createEntity(pEntityType, pRecord, (pError, pRecordBody) =>
		{
			if (pError) { return fReject(pError); }
			let tmpBodyError = _entityErrorFromBody(pRecordBody);
			if (tmpBodyError) { return fReject(tmpBodyError); }
			return fResolve(pRecordBody);
		});
	});
}

function updateEntity(pPict, pEntityType, pRecord)
{
	return new Promise((fResolve, fReject) =>
	{
		let tmpEntityProvider = _entityProvider(pPict);
		if (!tmpEntityProvider) { return fReject(new Error('Framework EntityProvider is not available.')); }
		tmpEntityProvider.updateEntity(pEntityType, pRecord, (pError, pRecordBody) =>
		{
			if (pError) { return fReject(pError); }
			let tmpBodyError = _entityErrorFromBody(pRecordBody);
			if (tmpBodyError) { return fReject(tmpBodyError); }
			return fResolve(pRecordBody);
		});
	});
}

function deleteEntity(pPict, pEntityType, pIDRecord)
{
	return new Promise((fResolve, fReject) =>
	{
		let tmpEntityProvider = _entityProvider(pPict);
		if (!tmpEntityProvider) { return fReject(new Error('Framework EntityProvider is not available.')); }
		tmpEntityProvider.deleteEntity(pEntityType, pIDRecord, (pError, pRecordBody) =>
		{
			if (pError) { return fReject(pError); }
			let tmpBodyError = _entityErrorFromBody(pRecordBody);
			if (tmpBodyError) { return fReject(tmpBodyError); }
			return fResolve(pRecordBody);
		});
	});
}

module.exports =
{
	getJSON,
	postJSON,
	putJSON,
	delJSON,
	getText,
	getBinary,
	uploadBinary,
	getEntity,
	createEntity,
	updateEntity,
	deleteEntity
};
